// src/pages/NotificationsPage.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isToday, isYesterday, isThisWeek } from 'date-fns';
import { Loader2 } from 'lucide-react';
import NotificationItem from '../components/NotificationItem';
import { getNotifications, markAllNotificationsAsRead } from '../services/notificationService';
import styles from './NotificationsPage.module.css';

const groupNotifications = (notifications, t) => {
  const groups = {
    [t('notifications.today')]: [],
    [t('notifications.yesterday')]: [],
    [t('notifications.this_week')]: [],
    [t('notifications.earlier')]: []
  };

  notifications.forEach((notification) => {
    const date = notification.createdAt && notification.createdAt.toDate
      ? notification.createdAt.toDate()
      : notification.createdAt instanceof Date
        ? notification.createdAt
        : new Date();

    if (isToday(date)) {
      groups[t('notifications.today')].push(notification);
    } else if (isYesterday(date)) {
      groups[t('notifications.yesterday')].push(notification);
    } else if (isThisWeek(date)) {
      groups[t('notifications.this_week')].push(notification);
    } else {
      groups[t('notifications.earlier')].push(notification);
    }
  });

  return groups;
};

const NotificationsPage = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedNotifications = await getNotifications();
      if (isMountedRef.current) {
        setNotifications(fetchedNotifications);
        setError(null);
      }

      markAllNotificationsAsRead().catch((err) => {
        console.error('Error marking notifications as read:', err);
      });
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (isMountedRef.current) {
        setError(t('notifications.error_loading'));
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    isMountedRef.current = true;
    loadNotifications();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadNotifications]);

  const groupedNotifications = groupNotifications(notifications, t);

  return (
    <div className={styles['notifications-page']}>
      <div className={styles['notifications-page__header']}>
        <h1 className={styles['notifications-page__title']}>
          {t('notifications.notifications')}
        </h1>
      </div>

      <div className={styles['notifications-page__content']}>
        {isLoading ? (
          <div className={styles['loading-state']}>
            <Loader2 className={styles['loading-spinner']} size={32} />
            <p>{t('notifications.loading')}</p>
          </div>
        ) : error ? (
          <div className={styles['error-state']}>
            <p>{error}</p>
            <button
              className={styles['retry-button']}
              onClick={loadNotifications}
              disabled={isLoading}
            >
              {t('retry')}
            </button>
          </div>
        ) : notifications.length > 0 ? (
          <div className={styles['notifications-list']}>
            {Object.entries(groupedNotifications).map(([group, groupItems]) => (
              groupItems.length > 0 && (
                <div key={group} className={styles['notification-group']}>
                  <h2 className={styles['group-header']}>{group}</h2>
                  <div className={styles['notification-group__items']}>
                    {groupItems.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <div className={styles['empty-state']}>
            <div className={styles['empty-icon']}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p>{t('notifications.caught_up')}</p>
            <p>{t('notifications.check_back_later')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
